import React from 'react';
import { useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { getPermissionsForRoute } from '../../utils/permissions';
import { getPermissionMessage } from '../../utils/permissionMessages';
import PermissionDeniedBanner from '../Common/PermissionDeniedBanner';

/**
 * PermissionProtectedRoute component that checks permissions before rendering a route
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The component to render if access is granted
 * @param {string|Array} props.permission - Required permission(s) for this route
 * @param {string} props.redirectTo - Where to redirect if access is denied (default: '/supplier/dashboard')
 * @param {React.ReactNode} props.fallback - Custom component to show if access is denied
 * @returns {React.ReactNode}
 */
const PermissionProtectedRoute = ({ 
  children, 
  permission, 
  redirectTo = '/supplier/dashboard',
  fallback = null 
}) => {
  const location = useLocation();
  const { can, canAny, _canAccess } = usePermissions();
  
  // If no permission specified, try to get permissions from the route
  let requiredPermissions = permission;
  
  if (!requiredPermissions) {
    requiredPermissions = getPermissionsForRoute(location.pathname);
  }
  
  // Convert to array if single permission
  const permissions = Array.isArray(requiredPermissions) 
    ? requiredPermissions 
    : [requiredPermissions];
  
  // Check if user has required permissions
  let hasAccess = false;
  
  if (permissions.length === 0) {
    hasAccess = true; // No permissions required
  } else if (permissions.length === 1) {
    hasAccess = can(permissions[0]);
  } else {
    hasAccess = canAny(permissions);
  }
  
  if (!hasAccess) {
    if (fallback) {
      return fallback;
    }
    
    // Use standardized permission denied banner
    return (
      <PermissionDeniedBanner
        {...getPermissionMessage('PAGE_ACCESS')}
        backButtonPath={redirectTo}
      />
    );
  }
  
  return children;
};

export default PermissionProtectedRoute;
